import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { FileText, Upload, Eye, Download, Trash2, AlertTriangle, Plus } from "lucide-react";
import { format } from "date-fns";
import { UploadDocumentDialog } from "../documents/UploadDocumentDialog";
import { DocumentPreviewModal } from "../documents/DocumentPreviewModal";

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
}

interface Document {
  id: string;
  name: string;
  file_url: string;
  category: string;
  file_size: number | null;
  mime_type: string | null;
  created_at: string;
  expires_at: string | null;
  is_required: boolean | null;
  uploaded_by: string | null;
}

interface EmployeeDocumentManagementProps {
  employee: Employee;
  documents: Document[];
  onDataUpdate: () => void;
}

export const EmployeeDocumentManagement: React.FC<EmployeeDocumentManagementProps> = ({
  employee,
  documents,
  onDataUpdate
}) => {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<Document | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const handlePreviewDocument = (document: Document) => {
    setPreviewDocument(document);
    setPreviewModalOpen(true);
  };

  const handleDownloadDocument = async (document: Document) => {
    try {
      // Get a signed URL for secure download
      const fileName = document.file_url.split('/').pop();
      if (!fileName) throw new Error('Invalid file URL');

      const { data, error } = await supabase.storage
        .from('employee-documents')
        .createSignedUrl(fileName, 3600); // 1 hour expiry

      if (error) throw error;

      // Create a temporary link and trigger download
      const linkElement = window.document.createElement('a');
      linkElement.href = data.signedUrl;
      linkElement.download = document.name;
      window.document.body.appendChild(linkElement);
      linkElement.click();
      window.document.body.removeChild(linkElement);

      toast.success('Download started');
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    if (!confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }

    try {
      // First get the document to find the file URL
      const { data: document, error: fetchError } = await supabase
        .from('employee_documents')
        .select('file_url')
        .eq('id', documentId)
        .single();

      if (fetchError) throw fetchError;

      // Delete from storage
      const fileName = document.file_url.split('/').pop();
      if (fileName) {
        await supabase.storage
          .from('employee-documents')
          .remove([fileName]);
      }

      // Delete from database
      const { error } = await supabase
        .from('employee_documents')
        .delete()
        .eq('id', documentId);

      if (error) throw error;
      
      toast.success('Document deleted successfully');
      onDataUpdate();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      contract: 'bg-blue-100 text-blue-800',
      identification: 'bg-green-100 text-green-800',
      certification: 'bg-purple-100 text-purple-800',
      tax_documents: 'bg-orange-100 text-orange-800',
      emergency_contact: 'bg-red-100 text-red-800',
      training: 'bg-yellow-100 text-yellow-800',
      other: 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors.other;
  };

  const isExpiringSoon = (expiresAt: string) => {
    if (!expiresAt) return false;
    const expireDate = new Date(expiresAt);
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return expireDate <= thirtyDaysFromNow;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Document Management</h3>
          <p className="text-sm text-muted-foreground">
            Manage employee documents, contracts, and certifications
          </p>
        </div>
        <Button onClick={() => setUploadDialogOpen(true)} className="flex items-center gap-2">
          <Upload className="h-4 w-4" />
          Upload Document
        </Button>
      </div>

      {/* Documents Grid */}
      {documents.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Documents Yet</h3>
            <p className="text-muted-foreground text-center mb-4">
              Upload documents to start building {employee.first_name}'s file
            </p>
            <Button onClick={() => setUploadDialogOpen(true)} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Upload First Document
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documents.map((document) => (
            <Card key={document.id} className="relative">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium truncate">{document.name}</h4>
                    <Badge 
                      className={`mt-1 ${getCategoryColor(document.category)}`}
                      variant="secondary"
                    >
                      {document.category.replace('_', ' ')}
                    </Badge>
                  </div>
                  {document.expires_at && isExpiringSoon(document.expires_at) && (
                    <div title="Expires soon">
                      <AlertTriangle className="h-4 w-4 text-orange-500 ml-2" />
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>Uploaded: {format(new Date(document.created_at), 'MMM dd, yyyy')}</p>
                  {document.expires_at && (
                    <p className={isExpiringSoon(document.expires_at) ? 'text-orange-600' : ''}>
                      Expires: {format(new Date(document.expires_at), 'MMM dd, yyyy')}
                    </p>
                  )}
                  {document.is_required && (
                    <Badge variant="outline" className="text-xs">Required</Badge>
                  )}
                </div>
                
                <div className="flex items-center gap-2 mt-4">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handlePreviewDocument(document)}
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    View
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleDownloadDocument(document)}
                  >
                    <Download className="h-3 w-3 mr-1" />
                    Download
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleDeleteDocument(document.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Upload Dialog */}
      <UploadDocumentDialog
        employee={employee}
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        onSuccess={onDataUpdate}
      />

      {/* Preview Modal */}
      <DocumentPreviewModal
        document={previewDocument}
        open={previewModalOpen}
        onOpenChange={setPreviewModalOpen}
      />
    </div>
  );
};